import type React from 'react'
import { Link, useInRouterContext } from 'react-router'

/**
 * The site footer every public page carries — one component, one markup, one
 * stylesheet block (index.css `.m-footer*`, de-scoped from the landing page
 * where the footer originally lived). "Get started" stays overridable so the
 * landing page can open its early-access modal instead of navigating.
 */

/**
 * Internal link that degrades to a plain anchor outside a Router context.
 * The app always renders inside BrowserRouter, but page tests render this
 * footer bare (Security/Changelog/… tests mount pages without a Router — the
 * same reason DocsLayout is Router-free), and react-router's Link throws
 * there. pushState + popstate keeps real SPA navigation when a router IS
 * present via Link; outside one, the href still works (full load in tests
 * is irrelevant — they never click).
 */
function FooterLink({ to, children, ...rest }: { to: string } & React.ComponentProps<'a'>) {
  const inRouter = useInRouterContext()
  if (inRouter) {
    return (
      <Link to={to} {...rest}>
        {children}
      </Link>
    )
  }
  return (
    <a href={to} {...rest}>
      {children}
    </a>
  )
}

export function SiteFooter({
  onGetStarted,
}: {
  onGetStarted?: (event: React.MouseEvent<HTMLAnchorElement>) => void
} = {}) {
  return (
    <footer className="m-footer" data-mol-id="site-footer">
      <div className="m-footer-inner">
        <div className="m-footer-top">
          <div className="m-footer-brand">
            <div className="m-foot-logo">Molecule.dev</div>
            <p>
              Over a thousand open-source packages AI understands. Real architecture. Built to
              scale.
            </p>
          </div>
          <div className="m-footer-cols">
            <div className="m-footer-col">
              <h3>Product</h3>
              <FooterLink to="/" onClick={onGetStarted}>
                Get started
              </FooterLink>
              <a
                href="https://github.com/molecule-dev/molecule"
                rel="noopener noreferrer"
                target="_blank"
              >
                GitHub
              </a>
              <FooterLink to="/#capabilities">Capabilities</FooterLink>
              <FooterLink to="/#faq">FAQ</FooterLink>
            </div>
            <div className="m-footer-col">
              <h3>Documentation</h3>
              {/* The docs section first — then the real surfaces: the catalog,
                  the gallery, and the CLI's official page (www.mlcl.dev 301s here). */}
              <FooterLink to="/docs">Docs</FooterLink>
              <FooterLink to="/mlcl">CLI (mlcl)</FooterLink>
              <FooterLink to="/templates">Templates</FooterLink>
              <FooterLink to="/packages">Packages</FooterLink>
              <a href="/llms-full.txt">Package reference</a>
              <FooterLink to="/changelog">Changelog</FooterLink>
              <FooterLink to="/security">Security</FooterLink>
            </div>
            <div className="m-footer-col">
              <h3>Company</h3>
              <a href="/blog/">Blog</a>
              <a href="mailto:contact@molecule.dev?subject=Hello!">Contact</a>
              <FooterLink to="/licensing">Licensing</FooterLink>
              <a href="https://status.molecule.dev">Status</a>
            </div>
            <div className="m-footer-col">
              <h3>Legal</h3>
              <FooterLink to="/privacy">Privacy</FooterLink>
              <FooterLink to="/terms">Terms</FooterLink>
              <FooterLink to="/dpa">DPA</FooterLink>
            </div>
          </div>
        </div>
        <div className="m-footer-bottom">
          <span>&copy; {new Date().getFullYear()} Molecule Dev, Inc.</span>
          <div className="m-footer-social">
            <a
              href="https://x.com/molecule_dev"
              rel="noopener noreferrer"
              target="_blank"
              aria-label="X"
            >
              <svg viewBox="0 0 24 24">
                <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z"></path>
              </svg>
            </a>
            <a
              href="https://discord.gg/bUs8YagpWe"
              rel="noopener noreferrer"
              target="_blank"
              aria-label="Discord"
            >
              <svg viewBox="0 0 24 24">
                <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189z"></path>
              </svg>
            </a>
            <a
              href="https://moleculedev.slack.com"
              rel="noopener noreferrer"
              target="_blank"
              aria-label="Slack"
            >
              <svg viewBox="0 0 24 24">
                <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z"></path>
              </svg>
            </a>
            <a
              href="https://github.com/molecule-dev/molecule"
              rel="noopener noreferrer"
              target="_blank"
              aria-label="GitHub"
            >
              <svg viewBox="0 0 16 16">
                <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z"></path>
              </svg>
            </a>
          </div>
        </div>
      </div>
    </footer>
  )
}
